// Gruppo.tsx — /g/<codice>: la partita a 4 (v013). Invito con il nome (anche senza account), permesso della
// fotocamera, poi la partita (sala d'attesa con i video di tutti, via, eliminazioni, classifica).
// Chi ha già il token del suo posto (l'ha creata, o rientra) va diretto alla partita.
import { AvvisoSospensione } from "../componenti/AvvisoSospensione.tsx";
import { ConsensoVolto, serveConsenso } from "../gioco/ConsensoVolto.tsx";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Faccina } from "../componenti/Faccina.tsx";
import { CampoNome, useNomeGiocatore } from "../gioco/CampoNome.tsx";
import { apriFotocamera, type ErroreFotocamera } from "../gioco/video.ts";
import { preparaRilevamento } from "../gioco/rilevamento.ts";
import { stanzaGruppo, useStanzaGruppo } from "../gioco/stanzaGruppo.ts";
import { entraGruppo, leggiGruppo, salvaTokenGruppo, tokenGruppo, type InfoGruppo } from "../servizi/gruppi.ts";
import { ErroreRete } from "../servizi/api.ts";
import { salvaPreferenza } from "../servizi/preferenze.ts";
import { vai } from "../servizi/percorso.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import { PREFERENZE } from "../costanti.ts";
import { PartitaGruppo } from "./PartitaGruppo.tsx";
import { codiceErroreGruppo } from "./CreaGruppo.tsx";

type Passo =
  | { p: "caricamento" }
  | { p: "invito"; info: InfoGruppo }
  | { p: "volto" }
  | { p: "fotocamera" }
  | { p: "errore_fotocamera"; errore: ErroreFotocamera }
  | { p: "stanza"; flusso: MediaStream }
  | { p: "errore"; testo: string };

export function Gruppo({ codice, tema }: { codice: string; tema: ControlloTema }) {
  const { t } = useTranslation();
  const [passo, setPasso] = useState<Passo>({ p: "caricamento" });
  const st = useStanzaGruppo();
  const flussoRif = useRef<MediaStream | null>(null);
  const montato = useRef(false);

  const chiudiFotocamera = useCallback(() => {
    flussoRif.current?.getTracks().forEach((tr) => tr.stop());
    flussoRif.current = null;
  }, []);

  const apri = useCallback(async () => {
    const token = tokenGruppo(codice);
    if (!token) return;
    // v021: prima della prima partita la spiegazione dell'analisi del volto, con la spunta.
    if (serveConsenso()) {
      setPasso({ p: "volto" });
      return;
    }
    setPasso({ p: "fotocamera" });
    void preparaRilevamento();
    try {
      const f = await apriFotocamera();
      if (!montato.current) {
        f.getTracks().forEach((tr) => tr.stop());
        return;
      }
      flussoRif.current = f;
      stanzaGruppo.entra(codice, token);
      setPasso({ p: "stanza", flusso: f });
    } catch (e) {
      setPasso({ p: "errore_fotocamera", errore: e as ErroreFotocamera });
    }
  }, [codice]);

  const avviato = useRef(false);
  useEffect(() => {
    if (avviato.current) return;
    avviato.current = true;
    if (tokenGruppo(codice)) {
      void apri();
      return;
    }
    leggiGruppo(codice)
      .then((info) => setPasso({ p: "invito", info }))
      .catch((e: unknown) => setPasso({ p: "errore", testo: t(`gruppo.errore_${e instanceof ErroreRete && e.stato === 404 ? "non_trovato" : codiceErroreGruppo(e)}`) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codice]);

  // Uscendo davvero dalla schermata: fotocamera spenta, fuori dalla stanza (il controllo dopo un attimo distingue
  // l'uscita vera dal doppio montaggio di React in sviluppo).
  useEffect(() => {
    montato.current = true;
    return () => {
      montato.current = false;
      setTimeout(() => {
        if (montato.current) return;
        chiudiFotocamera();
        if (stanzaGruppo.stato.passo !== "fuori") stanzaGruppo.esci();
      }, 0);
    };
  }, [chiudiFotocamera]);

  const esci = useCallback(() => {
    stanzaGruppo.esci();
    chiudiFotocamera();
    vai("/");
  }, [chiudiFotocamera]);

  if (passo.p === "stanza" && st.passo === "errore") {
    if (st.errore === "bloccato")
      return (
        <Messaggio tema={tema} testo="" onHome={esci}>
          <AvvisoSospensione seNo={<p className="schermata-testo" role="alert">{t("gioco.errore_bloccato")}</p>} />
        </Messaggio>
      );
    return <Messaggio tema={tema} testo={t(`gioco.errore_${st.errore ?? "generico"}`, { defaultValue: t("gioco.errore_generico") })} onHome={esci} />;
  }
  if (passo.p === "stanza") return <PartitaGruppo codice={codice} flusso={passo.flusso} onEsci={esci} />;
  if (passo.p === "errore") return <Messaggio tema={tema} testo={passo.testo} onHome={() => vai("/")} />;
  if (passo.p === "invito") return <Invito codice={codice} info={passo.info} tema={tema} onDentro={() => void apri()} />;
  if (passo.p === "volto") return <ConsensoVolto onAccettato={() => void apri()} onEsci={esci} />;
  if (passo.p === "errore_fotocamera") {
    return (
      <Messaggio tema={tema} testo={t(`gioco.fotocamera_${passo.errore}`)} onHome={esci}>
        <button type="button" className="pulsante pulsante-primario" onClick={() => void apri()}>
          {t("gioco.riprova")}
        </button>
      </Messaggio>
    );
  }
  return (
    <div className="pagina schermata-semplice" aria-busy="true">
      <Faccina tema={tema.tema} />
      <p className="schermata-testo">{passo.p === "fotocamera" ? t("gioco.fotocamera_richiesta") : t("generale.caricamento")}</p>
    </div>
  );
}

function Invito({ codice, info, tema, onDentro }: { codice: string; info: InfoGruppo; tema: ControlloTema; onDentro: () => void }) {
  const { t } = useTranslation();
  const [nome, setNome] = useNomeGiocatore(); // v039: anche il nome dell'account su un telefono nuovo
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  // v021: la spiegazione del volto prima di prendere il posto (chi dice «Annulla» non tiene un posto occupato)
  const [volto, setVolto] = useState(false);
  const entra = () => {
    const n = nome.trim();
    if (!n) return;
    salvaPreferenza(PREFERENZE.nome, n);
    setInvio(true);
    setErrore(null);
    entraGruppo(codice, n)
      .then((r) => {
        salvaTokenGruppo(codice, r.token);
        onDentro();
      })
      .catch((err: unknown) => {
        setErrore(codiceErroreGruppo(err));
        setInvio(false);
      });
  };
  if (info.stato === "chiuso") return <Messaggio tema={tema} testo={t("gruppo.non_si_entra_chiuso")} onHome={() => vai("/")} />;
  if (volto)
    return (
      <ConsensoVolto
        onAccettato={() => {
          setVolto(false);
          entra();
        }}
        onEsci={() => setVolto(false)}
      />
    );
  return (
    <div className="pagina invito">
      <main className="invito-centro">
        <Faccina tema={tema.tema} quale={info.avatarOrganizzatore ?? "mistero"} />
        <h1 className="invito-titolo">{t("gruppo.invito_titolo", { nome: info.organizzatore ?? "" })}</h1>
        <p className="invito-testo">{t("gruppo.come_funziona")}</p>
        <p className="nota-centrata">{t("gruppo.invito_posti", { n: info.posti, totale: info.massimo })}</p>
        <form
          className="invito-modulo"
          onSubmit={(e) => {
            e.preventDefault();
            if (!nome.trim()) return;
            if (serveConsenso()) setVolto(true);
            else entra();
          }}
        >
          <CampoNome valore={nome} onCambia={setNome} />
          {errore === "bloccato" ? (
            <AvvisoSospensione seNo={<p className="admin-errore" role="alert">{t("gruppo.errore_bloccato")}</p>} />
          ) : errore && (
            <p className="admin-errore" role="alert">
              {t(`gruppo.errore_${errore}`)}
            </p>
          )}
          <button type="submit" className="pulsante pulsante-primario" disabled={invio}>
            {t("gruppo.entra")}
          </button>
          <button type="button" className="pulsante pulsante-secondario" onClick={() => vai("/")}>
            {t("gioco.no_grazie")}
          </button>
        </form>
      </main>
    </div>
  );
}

function Messaggio({ tema, testo, onHome, children }: { tema: ControlloTema; testo: string; onHome: () => void; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="pagina schermata-semplice">
      <Faccina tema={tema.tema} />
      {testo && (
        <p className="schermata-testo" role="alert">
          {testo}
        </p>
      )}
      <div className="azioni-colonna">
        {children}
        <button type="button" className="pulsante pulsante-secondario" onClick={onHome}>
          {t("gioco.torna_home")}
        </button>
      </div>
    </div>
  );
}
